import { supabase } from '@/lib/supabaseClient';
import {
  MedicineProductRecord,
  MedicineReceiptRecord,
  HealthEventRecord,
} from '@/types/database';
import { documentService } from '@/services/documentService';

export interface CreateMedicineReceiptPayload {
  supplier_id: string;
  product_id: string;
  cycle_id?: string | null;
  farm_id?: string | null;
  receipt_date: string;
  quantity: number;
  unit_measurement: string;
  unit_price: number;
  category_id?: string | null;
  account_code?: string | null;
  invoice_no?: string | null;
  notes?: string | null;
}

export interface CreateHealthEventPayload {
  cycle_id: string;
  event_date: string;
  event_type: string; // flexible string
  product_id?: string | null;
  category_id?: string | null;
  head_count_treated?: number | null;
  total_cost?: number;
  diagnostic_result?: string | null; // flexible string
  veterinarian_technician?: string | null;
  notes?: string | null;
}

export const healthService = {
  /**
   * Non-authoritative UI convenience suggestion helpers.
   * These are purely client-side suggestions to speed up data entry.
   * Arbitrary custom text entry and actual field observations are 100% supported and stored directly.
   */
  getSuggestedEventTypes(): string[] {
    return ['ASF Diagnostic Test', 'ASF Vaccine', 'Antibiotic Treatment', 'Routine Vaccine', 'Vitamins / Minerals', 'Disinfection / Biosecurity'];
  },

  getSuggestedDiagnosticResults(): string[] {
    return ['Negative (ASF-Free)', 'Positive (ASF Detected)', 'Pending / In-Lab Analysis', 'Non-Diagnostic / Treatment'];
  },

  /**
   * Fetches active medicine transaction categories (COA 3030-3032 etc.) from transaction_categories.
   */
  async getMedicineTransactionCategories() {
    const { data, error } = await supabase
      .from('transaction_categories')
      .select('id, code, name, category_type, target_table, is_active')
      .eq('is_void', false)
      .eq('is_active', true)
      .order('code', { ascending: true });

    if (error) {
      console.warn('[healthService.getMedicineTransactionCategories] Warn:', error.message);
      return [];
    }

    return data || [];
  },

  /**
   * Fetches active medicine products catalog.
   */
  async getMedicineProducts(): Promise<MedicineProductRecord[]> {
    const { data, error } = await supabase
      .from('medicine_products')
      .select(`
        id,
        product_code,
        product_name,
        category,
        supplier_id,
        unit_measurement,
        default_unit_price,
        notes,
        is_active,
        is_void,
        suppliers (id, supplier_name)
      `)
      .eq('is_void', false)
      .eq('is_active', true)
      .order('product_name', { ascending: true });

    if (error) {
      console.warn('[healthService.getMedicineProducts] Warn:', error.message);
      return [];
    }

    return (data || []) as unknown as MedicineProductRecord[];
  },

  /**
   * Fetches medicine receipts with joined relations and documents.
   */
  async getMedicineReceipts(cycleId?: string, farmId?: string): Promise<MedicineReceiptRecord[]> {
    let query = supabase
      .from('medicine_receipts')
      .select(`
        *,
        suppliers (id, supplier_code, supplier_name),
        medicine_products (id, product_code, product_name, category, unit_measurement),
        farms (id, farm_name),
        production_cycles (id, cycle_code, status, operation_type, growers (full_name)),
        transaction_categories (id, code, name)
      `)
      .eq('is_void', false)
      .order('receipt_date', { ascending: false });

    if (cycleId) {
      query = query.eq('cycle_id', cycleId);
    }
    if (farmId) {
      query = query.eq('farm_id', farmId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[healthService.getMedicineReceipts] Error:', error.message);
      throw error;
    }

    const receipts = (data || []) as unknown as MedicineReceiptRecord[];

    for (const receipt of receipts) {
      try {
        const docs = await documentService.getEntityDocuments('medicine_receipts', receipt.id);
        receipt.documents = docs;
      } catch {
        receipt.documents = [];
      }
    }

    return receipts;
  },

  /**
   * Fetches health events for a cycle with joined relations and documents.
   */
  async getHealthEvents(cycleId?: string): Promise<HealthEventRecord[]> {
    let query = supabase
      .from('health_events')
      .select(`
        *,
        medicine_products (id, product_code, product_name, category, unit_measurement),
        transaction_categories (id, code, name),
        production_cycles (id, cycle_code, status, operation_type, growers (full_name))
      `)
      .eq('is_void', false)
      .order('event_date', { ascending: false });

    if (cycleId) {
      query = query.eq('cycle_id', cycleId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[healthService.getHealthEvents] Error:', error.message);
      throw error;
    }

    const events = (data || []) as unknown as HealthEventRecord[];

    for (const event of events) {
      try {
        const docs = await documentService.getEntityDocuments('health_events', event.id);
        event.documents = docs;
      } catch {
        event.documents = [];
      }
    }

    return events;
  },

  /**
   * Records a new medicine receipt from a supplier.
   */
  async createMedicineReceipt(
    payload: CreateMedicineReceiptPayload,
    attachedFile?: File | Blob | null
  ): Promise<MedicineReceiptRecord> {
    if (!payload.supplier_id) throw new Error('Supplier is required.');
    if (!payload.product_id) throw new Error('Medicine product is required.');
    if (!payload.receipt_date) throw new Error('Receipt date is required.');
    if (!payload.quantity || payload.quantity === 0) {
      throw new Error('Quantity cannot be zero.');
    }
    if (payload.unit_price < 0) throw new Error('Unit price cannot be negative.');

    const totalAmount = Number(payload.quantity) * Number(payload.unit_price);
    const receiptNumber = `MREC-${Date.now().toString().slice(-6)}`;

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const insertPayload: any = {
      receipt_number: receiptNumber,
      supplier_id: payload.supplier_id,
      product_id: payload.product_id,
      cycle_id: payload.cycle_id || null,
      farm_id: payload.farm_id || null,
      receipt_date: payload.receipt_date,
      quantity: Number(payload.quantity),
      unit_measurement: payload.unit_measurement || 'vial',
      unit_price: Number(payload.unit_price),
      total_amount: totalAmount,
      account_code: payload.account_code || null,
      category_id: payload.category_id || null,
      invoice_no: payload.invoice_no?.trim() || null,
      notes: payload.notes?.trim() || null,
      created_by: userId,
      updated_by: userId,
      is_void: false,
    };

    const { data: receiptRecord, error: insertError } = await supabase
      .from('medicine_receipts')
      .insert(insertPayload)
      .select(`
        *,
        suppliers (id, supplier_code, supplier_name),
        medicine_products (id, product_code, product_name, unit_measurement)
      `)
      .single();

    if (insertError) {
      console.error('[healthService.createMedicineReceipt] Insert error:', insertError);
      throw new Error(`Failed to create medicine receipt: ${insertError.message}`);
    }

    if (attachedFile && receiptRecord?.id) {
      try {
        const fileName = (attachedFile as File).name || `medicine_invoice_${receiptNumber}.jpg`;
        await documentService.uploadDocument({
          file: attachedFile,
          fileName,
          entityType: 'medicine_receipts',
          entityId: receiptRecord.id,
          documentType: 'invoice',
          notes: `Medicine invoice for ${receiptNumber}`,
        });
      } catch (docErr: any) {
        console.error('[healthService.createMedicineReceipt] Document attachment error:', docErr);
      }
    }

    return receiptRecord as unknown as MedicineReceiptRecord;
  },

  /**
   * Records a new health, treatment, or ASF diagnostic event.
   */
  async createHealthEvent(
    payload: CreateHealthEventPayload,
    attachedFile?: File | Blob | null
  ): Promise<HealthEventRecord> {
    if (!payload.cycle_id) throw new Error('Production Cycle is required.');
    if (!payload.event_date) throw new Error('Event date is required.');
    if (!payload.event_type || !payload.event_type.trim()) {
      throw new Error('Event type / description is required.');
    }

    const eventNumber = `HLTH-${Date.now().toString().slice(-6)}`;

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const insertPayload: any = {
      event_number: eventNumber,
      cycle_id: payload.cycle_id,
      event_date: payload.event_date,
      event_type: payload.event_type.trim(),
      product_id: payload.product_id || null,
      category_id: payload.category_id || null,
      head_count_treated: payload.head_count_treated ? Number(payload.head_count_treated) : null,
      total_cost: Number(payload.total_cost) || 0,
      diagnostic_result: payload.diagnostic_result?.trim() || null,
      veterinarian_technician: payload.veterinarian_technician?.trim() || null,
      notes: payload.notes?.trim() || null,
      created_by: userId,
      updated_by: userId,
      is_void: false,
    };

    const { data: eventRecord, error: insertError } = await supabase
      .from('health_events')
      .insert(insertPayload)
      .select(`
        *,
        medicine_products (id, product_code, product_name),
        production_cycles (id, cycle_code)
      `)
      .single();

    if (insertError) {
      console.error('[healthService.createHealthEvent] Insert error:', insertError);
      throw new Error(`Failed to create health event: ${insertError.message}`);
    }

    if (attachedFile && eventRecord?.id) {
      try {
        const fileName = (attachedFile as File).name || `health_cert_${eventNumber}.jpg`;
        await documentService.uploadDocument({
          file: attachedFile,
          fileName,
          entityType: 'health_events',
          entityId: eventRecord.id,
          documentType: 'lab_result',
          notes: `Diagnostic report / certificate for ${eventNumber}`,
        });
      } catch (docErr: any) {
        console.error('[healthService.createHealthEvent] Document attachment error:', docErr);
      }
    }

    return eventRecord as unknown as HealthEventRecord;
  },

  /**
   * Soft-voids a medicine receipt with mandatory audit reason.
   */
  async softVoidMedicineReceipt(receiptId: string, voidReason: string): Promise<void> {
    if (!voidReason || !voidReason.trim()) {
      throw new Error('Mandatory void reason is required.');
    }

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const { error } = await supabase
      .from('medicine_receipts')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: new Date().toISOString(),
        voided_by: userId,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', receiptId);

    if (error) {
      throw new Error(`Failed to void medicine receipt: ${error.message}`);
    }
  },

  /**
   * Soft-voids a health event with mandatory audit reason.
   */
  async softVoidHealthEvent(eventId: string, voidReason: string): Promise<void> {
    if (!voidReason || !voidReason.trim()) {
      throw new Error('Mandatory void reason is required.');
    }

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const { error } = await supabase
      .from('health_events')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: new Date().toISOString(),
        voided_by: userId,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', eventId);

    if (error) {
      throw new Error(`Failed to void health event: ${error.message}`);
    }
  },
};
